import { useState, useCallback } from 'react';

// WHY: The app supports two SANSARA themes (light + dark). useTheme reports the
// active theme and exposes toggleTheme, which flips the `dark` class on <html>
// (via Tailwind's class strategy) and persists the choice to localStorage. The
// initial theme is resolved once before paint in main.tsx; default is dark.
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'dnd-theme';

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}

export { getInitialTheme };
